import {
  DEFAULT_COLLECTION_NAME,
  DEFAULT_PARTITION_NAME,
  SALES_HUB_DEFAULT_COLLECTION_LABEL,
} from '@/utils/order-guide';

export const NAME_MAX_LENGTH = 40;

// Lowercase and strip whitespace so reserved-name and duplicate checks treat
// "Order Guide", "order guide", and "orderguide" as the same name.
const normalizeName = (name: string): string =>
  name.trim().toLowerCase().replace(/\s/g, '');

/**
 * Validates a new partition (group) name: rejects empty names, names longer
 * than the max length, the reserved default name, and duplicates of existing
 * group names. Reserved and duplicate checks are case-insensitive and
 * whitespace-collapsed.
 *
 * @param name - Candidate name (trimmed defensively before checks).
 * @param existingNames - Names of the guide's current partitions.
 * @returns Whether the name can be used for a new partition.
 */
export const validatePartitionName = (
  name: string,
  existingNames: string[],
): boolean => {
  const trimmed = name.trim();
  const normalized = normalizeName(name);
  return (
    trimmed.length > 0 &&
    trimmed.length <= NAME_MAX_LENGTH &&
    normalized !== DEFAULT_PARTITION_NAME &&
    !existingNames.some((existing) => normalizeName(existing) === normalized)
  );
};

export type CollectionNameError =
  | 'duplicate'
  | 'empty'
  | 'reserved'
  | 'too_long';

/**
 * Validates a new order guide collection name and the reason it was rejected
 * or null when the name is usable.
 *
 * @param name - Candidate name
 * @param existingNames - Names of the current collections.
 * @returns The rejection reason, or null if the name can be used.
 */
export const validateCollectionName = (
  name: string,
  existingNames: string[],
): CollectionNameError | null => {
  const trimmed = name.trim();
  const normalized = normalizeName(name);
  if (trimmed.length === 0) {
    return 'empty';
  }
  if (trimmed.length > NAME_MAX_LENGTH) {
    return 'too_long';
  }
  if (
    normalized === DEFAULT_COLLECTION_NAME ||
    normalized === SALES_HUB_DEFAULT_COLLECTION_LABEL
  ) {
    return 'reserved';
  }
  if (
    existingNames.some((existing) => normalizeName(existing) === normalized)
  ) {
    return 'duplicate';
  }
  return null;
};
